import { useState, useRef, useEffect } from 'react';
import { STUDENT_STATUSES, TRIAL_STAGES } from '../../utils/constants';
import { getRumaalColor, getStudentStatusColor } from '../../utils/colors';
import RumaalRankSelect from '../common/RumaalRankSelect';
import FieldIcon, { Glyph } from '../common/FieldIcon';
import {
  parentEmailsToText,
  splitParentEmails,
  invalidParentEmails,
} from '../../utils/parentEmails';

// Shared field styling. Kept in one place so every box on this screen focuses
// the same way — without it each input falls back to the browser's blue ring,
// which fights the purple used everywhere else in the panel.
const FIELD_BASE =
  'w-full rounded-md border bg-white transition-shadow duration-150 outline-none ' +
  'placeholder:text-gray-400 focus:ring-2';
const FIELD_NORMAL = 'border-gray-200 focus:border-purple-400 focus:ring-purple-500/15';
const FIELD_INVALID = 'border-red-300 bg-red-50/60 focus:border-red-400 focus:ring-red-500/15';

const field = (invalid = false) => `${FIELD_BASE} ${invalid ? FIELD_INVALID : FIELD_NORMAL}`;

// Icon buttons in a student row: no visible chrome until pointed at.
const ROW_ACTION =
  'shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/30';

// Helper function to check if a rank is higher than another
const isRankHigher = (newRank, oldRank) => {
  const rankOrder = ['White', 'Yellow', 'Green', 'Blue', 'Red', 'Black'];
  const newIndex = rankOrder.indexOf(newRank);
  const oldIndex = rankOrder.indexOf(oldRank);
  return newIndex > oldIndex && newIndex !== -1 && oldIndex !== -1;
};

const StudentList = ({ students = [], isEditing, onStudentsChange, instructor = '', onMoveStudent, highlightStudent }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  // When arriving from search, scroll to the target student row and flash it.
  const highlightRef = useRef(null);
  const [flashId, setFlashId] = useState(null);

  // Drag-to-reorder state. dragEnabled gates the row's draggable attribute so
  // it only activates when the grip handle is pressed — leaving the name/notes
  // inputs freely selectable while editing.
  const [dragEnabled, setDragEnabled] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox won't start a drag unless some data is set.
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === targetIndex) return;
    const reordered = [...students];
    const [moved] = reordered.splice(draggingIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    onStudentsChange(reordered);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
    setDragEnabled(false);
  };

  // If the mouse is released outside the grip (no drag started), the grip's
  // own onMouseUp never fires and rows would stay draggable — reset globally.
  useEffect(() => {
    if (!dragEnabled) return;
    const reset = () => setDragEnabled(false);
    document.addEventListener('mouseup', reset);
    return () => document.removeEventListener('mouseup', reset);
  }, [dragEnabled]);

  useEffect(() => {
    const targetId = highlightStudent?.id;
    if (!targetId) return;
    setFlashId(targetId);
    // Wait a tick so the panel/list is rendered, then scroll the row into view.
    const scrollTimer = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    // Remove the flash after the animation finishes.
    const clearTimer = setTimeout(() => setFlashId(null), 2600);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
    // ts changes on every search click so re-selecting the same student re-fires.
  }, [highlightStudent?.id, highlightStudent?.ts]);

  const emptyNewStudent = {
    name: '',
    rumaalRank: 'White',
    status: 'Active',
    notes: '',
    parentEmails: '',
  };

  const [newStudent, setNewStudent] = useState(emptyNewStudent);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const newStudentBadEmails = invalidParentEmails(newStudent.parentEmails);
  const withParentEmail = students.filter(
    (s) => splitParentEmails(s.parentEmails).length > 0
  ).length;

  const handleAddStudent = () => {
    if (!newStudent.name.trim()) {
      alert('Please enter a student name');
      return;
    }
    if (newStudentBadEmails.length) {
      alert(`Not a valid email address: ${newStudentBadEmails.join(', ')}`);
      return;
    }

    const student = {
      id: `student-${Date.now()}`,
      ...newStudent,
      addedAt: new Date().toISOString(), // Track when student was added
      // Set trial stage to "In Progress" if status is Trial
      ...(newStudent.status === 'Trial' && { 
        trialStage: 'In Progress',
        rumaalRank: null, // Trial students don't have Rumaal rank
      }),
      // Ensure rumaalRank is set for non-Trial students
      ...(newStudent.status !== 'Trial' && !newStudent.rumaalRank && { rumaalRank: 'White' }),
    };

    // Update local state only - no API call
    const updatedStudents = [...students, student];
    onStudentsChange(updatedStudents);

    setNewStudent(emptyNewStudent);
    setShowAddForm(false);
  };

  const handleUpdateStudent = (studentId, field, value) => {
    // Update local state only - no API call
    const updatedStudents = students.map((s) => {
      if (s.id === studentId || s._id === studentId) {
        const updatedStudent = { ...s, [field]: value };
        
        // Track status changes
        if (field === 'status') {
          // Store previous status before change
          updatedStudent.previousStatus = s.status;
          
          // Track conversion: Trial -> Active
          if (s.status === 'Trial' && value === 'Active') {
            // Only set conversion if not already converted (tracking going forward)
            if (!s.convertedAt) {
              updatedStudent.convertedAt = new Date().toISOString();
              updatedStudent.convertedBy = instructor || 'Unknown';
            }
          }
          
          // Track when status changes to Paused
          if (value === 'Paused' && s.status !== 'Paused') {
            updatedStudent.pausedAt = new Date().toISOString();
          }
          
          // Track cancellation: when status changes from Active to non-active (except Paused which is tracked separately)
          // Or when removed from Active/Trial status
          if ((s.status === 'Active' || s.status === 'Trial') && 
              value !== 'Active' && value !== 'Trial' && value !== 'Paused') {
            // This might indicate cancellation/leaving
            if (!s.canceledAt) {
              updatedStudent.canceledAt = new Date().toISOString();
            }
          }
          // Clear trial stage when converted to Active
          updatedStudent.trialStage = null;
          // Require Rumaal rank when converting to Active (assign default if not set)
          if (!updatedStudent.rumaalRank) {
            updatedStudent.rumaalRank = 'White'; // Default rank when converting
          }
        }
        
        // Track when student is paused
        if (field === 'status' && value === 'Paused' && s.status !== 'Paused') {
          // Only set pausedAt if not already set (tracking going forward)
          if (!s.pausedAt) {
            updatedStudent.pausedAt = new Date().toISOString();
          }
        }
        
        // Set trial stage to "In Progress" when status changes to Trial
        if (field === 'status' && value === 'Trial' && s.status !== 'Trial') {
          updatedStudent.trialStage = 'In Progress';
          // Clear Rumaal rank when changing to Trial (only assigned at conversion)
          updatedStudent.rumaalRank = null;
        }
        
        // Prevent setting Rumaal rank for Trial students
        if (field === 'rumaalRank' && s.status === 'Trial') {
          // Don't allow Rumaal rank changes for Trial students
          return s; // Return unchanged student
        }
        
        // Clear trial stage when status changes from Trial to something else
        if (field === 'status' && s.status === 'Trial' && value !== 'Trial') {
          updatedStudent.trialStage = null;
          updatedStudent.trialAttendedAt = null;
          updatedStudent.trialCompletedAt = null;
        }
        
        // Track trial stage changes
        if (field === 'trialStage') {
          // Track when trial is attended
          if (value === 'Attending' && s.trialStage !== 'Attending') {
            if (!s.trialAttendedAt) {
              updatedStudent.trialAttendedAt = new Date().toISOString();
            }
          }
          // Track when trial is completed
          if (value === 'Attended' && s.trialStage !== 'Attended') {
            if (!s.trialCompletedAt) {
              updatedStudent.trialCompletedAt = new Date().toISOString();
            }
          }
        }
        
        // Track Rumaal upgrade: when rank increases
        if (field === 'rumaalRank' && s.rumaalRank && isRankHigher(value, s.rumaalRank)) {
          // Only track if not already upgraded (tracking going forward, first upgrade only)
          if (!s.rumaalUpgradedAt) {
            updatedStudent.rumaalUpgradedAt = new Date().toISOString();
            updatedStudent.rumaalUpgradedBy = instructor || 'Unknown';
            updatedStudent.previousRumaalRank = s.rumaalRank;
          }
        }
        
        return updatedStudent;
      }
      return s;
    });
    onStudentsChange(updatedStudents);
  };

  const handleDeleteStudent = (studentId, studentName) => {
    setDeleteConfirm({ id: studentId, name: studentName });
  };

  const confirmDelete = () => {
    // Mark student as canceled before deletion
    const updatedStudents = students.map((s) => {
      if (s.id === deleteConfirm.id || s._id === deleteConfirm.id) {
        return {
          ...s,
          canceledAt: new Date().toISOString(),
          previousStatus: s.status,
        };
      }
      return s;
    });
    
    // Then remove from list
    const filteredStudents = updatedStudents.filter(
      (s) => s.id !== deleteConfirm.id && s._id !== deleteConfirm.id
    );
    onStudentsChange(filteredStudents);
    setDeleteConfirm(null);
  };


  return (
    <div>
      <div className="mb-2.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
            Students
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {students.length}
            </span>
          </h3>
          {/* Every child needs a parent email before invites can go out, and the
              list is long enough that "how many are left" is worth showing. */}
          {students.length > 0 && (
            <p className="mt-0.5 text-xs text-gray-500">
              {withParentEmail === students.length
                ? 'All have a parent email'
                : `${withParentEmail} of ${students.length} have a parent email`}
            </p>
          )}
        </div>
        {isEditing && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            title={showAddForm ? 'Cancel' : 'Add a student'}
            aria-label={showAddForm ? 'Cancel adding a student' : 'Add a student'}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-all hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/40 ${
              showAddForm ? 'rotate-45 bg-gray-400 hover:bg-gray-500' : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
            </svg>
          </button>
        )}
      </div>

      {/* Add Student Form */}
      {showAddForm && isEditing && (
        <div className="mb-3 space-y-2 rounded-lg border border-purple-200 bg-purple-50/40 p-3 shadow-sm">
          <div className="group relative">
            <FieldIcon name="name" size="md" />
            <input
              type="text"
              value={newStudent.name}
              onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
              placeholder="Student name"
              autoFocus
              className={`${field()} py-1.5 pl-8 pr-2.5 text-sm`}
            />
          </div>
          <div className={`grid gap-2 ${newStudent.status === 'Trial' ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {/* Rumaal Rank - Only show for non-Trial students */}
            {newStudent.status !== 'Trial' && (
              <RumaalRankSelect
                value={newStudent.rumaalRank}
                onChange={(rank) => setNewStudent({ ...newStudent, rumaalRank: rank })}
                size="sm"
                fullWidth
              />
            )}
            <select
              value={newStudent.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                // Clear rumaalRank when changing to Trial
                if (newStatus === 'Trial') {
                  setNewStudent({ ...newStudent, status: newStatus, rumaalRank: null });
                } else {
                  // Set default rumaalRank for non-Trial students
                  setNewStudent({ 
                    ...newStudent, 
                    status: newStatus, 
                    rumaalRank: newStudent.rumaalRank || 'White' 
                  });
                }
              }}
              className={`${field()} py-1.5 px-2.5 text-sm`}
            >
              {STUDENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="group relative">
              <FieldIcon name="email" size="md" />
              <input
                type="text"
                value={newStudent.parentEmails}
                onChange={(e) => setNewStudent({ ...newStudent, parentEmails: e.target.value })}
                placeholder="Parent email — for the results portal login"
                className={`${field(newStudentBadEmails.length > 0)} py-1.5 pl-8 pr-2.5 text-sm`}
              />
            </div>
            <p className={`mt-1 text-xs ${newStudentBadEmails.length ? 'text-red-600' : 'text-gray-500'}`}>
              {newStudentBadEmails.length
                ? `Not a valid email: ${newStudentBadEmails.join(', ')}`
                : 'Separate two parents with a comma. Siblings sharing an email get one login.'}
            </p>
          </div>
          <div className="group relative">
            <FieldIcon name="notes" size="md" alignTop />
            <textarea
              value={newStudent.notes}
              onChange={(e) => setNewStudent({ ...newStudent, notes: e.target.value })}
              placeholder="Notes (optional) — allergies, reminders, parent contact, etc."
              rows={2}
              className={`${field()} resize-y py-1.5 pl-8 pr-2.5 text-sm`}
            />
          </div>
          <div className="flex gap-2 pt-0.5">
            <button
              onClick={handleAddStudent}
              className="flex-1 rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/40"
            >
              Add student
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewStudent(emptyNewStudent);
              }}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/30"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Student List */}
      <div className="space-y-2 max-h-96 overflow-y-auto pr-0.5">
        {students.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center">
            <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <Glyph name="name" size="md" />
            </div>
            <p className="text-sm text-gray-500">No students in this class yet</p>
          </div>
        ) : (
          students.map((student, index) => {
            const studentId = student.id || student._id;
            const isHighlighted = flashId != null && (student._id === flashId || student.id === flashId);
            const isDragging = draggingIndex === index;
            const isDragOver = dragOverIndex === index && draggingIndex !== null && draggingIndex !== index;
            return (
            <div
              key={studentId}
              ref={isHighlighted ? highlightRef : null}
              draggable={isEditing && dragEnabled}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => isEditing && handleDragOver(e, index)}
              onDrop={(e) => isEditing && handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`group/row flex items-start gap-2 ${isDragging ? 'opacity-40' : ''}`}
            >
              {/* Position in the class, set in the margin rather than inside the
                  card so it reads as an index of the list and not as another
                  piece of the student's data. Shares its slot with the drag
                  handle: the number gives way to the grip on hover, so
                  reordering costs no extra width. Right-aligned and tabular so
                  9 and 10 line up on the units digit. */}
              <div className={`relative w-6 shrink-0 ${isEditing ? 'mt-3' : 'mt-2.5'}`}>
                <span
                  className={`block text-right text-sm font-semibold leading-5 tabular-nums text-gray-400 ${
                    isEditing ? 'transition-opacity group-hover/row:opacity-0' : ''
                  }`}
                >
                  {index + 1}
                </span>
                {isEditing && (
                  <button
                    type="button"
                    onMouseDown={() => setDragEnabled(true)}
                    onMouseUp={() => setDragEnabled(false)}
                    className="absolute inset-0 flex cursor-grab touch-none items-center justify-center rounded text-gray-300 opacity-0 transition-opacity hover:text-gray-500 group-hover/row:opacity-100 focus-visible:opacity-100 focus-visible:outline-none active:cursor-grabbing"
                    title="Drag to reorder"
                    aria-label={`Drag to reorder ${student.name}`}
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z" />
                    </svg>
                  </button>
                )}
              </div>
              <div
                className={`flex min-w-0 flex-1 items-start gap-1.5 rounded-lg border p-2.5 transition-all ${
                  isHighlighted
                    ? 'student-flash border-orange-300'
                    : 'border-gray-200 bg-gray-50/70 group-hover/row:border-gray-300 group-hover/row:bg-gray-50 group-hover/row:shadow-sm'
                } ${
                  isDragOver
                    ? `border-purple-400 bg-purple-50 ${draggingIndex > index ? 'border-t-2' : 'border-b-2'}`
                    : ''
                }`}
              >
              <div className="min-w-0 flex-1 space-y-1.5">
                {isEditing ? (
                  <div className="group relative">
                    <FieldIcon name="name" />
                    <input
                      type="text"
                      value={student.name}
                      onChange={(e) =>
                        handleUpdateStudent(studentId, 'name', e.target.value)
                      }
                      placeholder="Student name"
                      className={`${field()} py-1 pl-7 pr-2.5 text-sm font-medium text-gray-800`}
                    />
                  </div>
                ) : (
                  <p className="truncate text-sm font-medium text-gray-800">{student.name}</p>
                )}
                <div className="flex items-center gap-2">
                  {/* Rumaal Rank Badge - Only show for non-Trial students */}
                  {student.status !== 'Trial' && (
                    isEditing ? (
                      <RumaalRankSelect
                        value={student.rumaalRank || 'White'}
                        onChange={(rank) =>
                          handleUpdateStudent(studentId, 'rumaalRank', rank)
                        }
                        size="xs"
                      />
                    ) : (
                      <span
                        className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                        style={{
                          backgroundColor: getRumaalColor(student.rumaalRank || 'White'),
                          color:
                            (student.rumaalRank || 'White') === 'White' || (student.rumaalRank || 'White') === 'Yellow'
                              ? '#000'
                              : '#fff',
                        }}
                      >
                        {student.rumaalRank || 'White'}
                      </span>
                    )
                  )}

                  {/* Status Badge */}
                  {isEditing ? (
                    <select
                      value={student.status}
                      onChange={(e) =>
                        handleUpdateStudent(studentId, 'status', e.target.value)
                      }
                      className="rounded-md border border-gray-200 px-1.5 py-0.5 text-xs outline-none transition-shadow focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15"
                      style={{
                        backgroundColor: getStudentStatusColor(student.status),
                        color: '#374151', // Dark grey text for readability on pastel backgrounds
                      }}
                    >
                      {STUDENT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span 
                      className="rounded-full px-2 py-0.5 text-xs font-medium text-gray-700"
                      style={{
                        backgroundColor: getStudentStatusColor(student.status),
                      }}
                    >
                      {student.status}
                    </span>
                  )}

                  {/* Trial Stage Badge - Only show when status is Trial */}
                  {student.status === 'Trial' && (
                    isEditing ? (
                      <select
                        value={student.trialStage || 'In Progress'}
                        onChange={(e) =>
                          handleUpdateStudent(studentId, 'trialStage', e.target.value)
                        }
                        className="rounded-md border border-gray-200 bg-blue-50 px-1.5 py-0.5 text-xs outline-none transition-shadow focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15"
                      >
                        {TRIAL_STAGES.map((stage) => (
                          <option key={stage} value={stage}>
                            {stage}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {student.trialStage || 'In Progress'}
                      </span>
                    )
                  )}

                  {/* Instagram badge — how many linked posts feature this student */}
                  {student.instagramPostCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2 py-0.5 text-xs font-medium text-pink-600 ring-1 ring-pink-200"
                      title={`Featured in ${student.instagramPostCount} Instagram ${
                        student.instagramPostCount === 1 ? 'post' : 'posts'
                      }`}
                    >
                      <Glyph name="instagram" />
                      {student.instagramPostCount}
                    </span>
                  )}
                </div>

                {/* Parent email — the portal login for this child's parent */}
                {isEditing ? (
                  <>
                    <div className="group relative">
                      <FieldIcon name="email" />
                      <input
                        type="text"
                        value={parentEmailsToText(student.parentEmails)}
                        onChange={(e) =>
                          handleUpdateStudent(studentId, 'parentEmails', e.target.value)
                        }
                        placeholder="Parent email"
                        className={`${field(invalidParentEmails(student.parentEmails).length > 0)} py-1 pl-7 pr-2.5 text-xs`}
                      />
                    </div>
                    {invalidParentEmails(student.parentEmails).length > 0 && (
                      <p className="text-xs text-red-600">
                        Not a valid email: {invalidParentEmails(student.parentEmails).join(', ')}
                      </p>
                    )}
                  </>
                ) : (
                  splitParentEmails(student.parentEmails).length > 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Glyph name="email" className="shrink-0 text-gray-400" />
                      <span className="truncate">
                        {splitParentEmails(student.parentEmails).join(', ')}
                      </span>
                    </p>
                  )
                )}

                {/* Student notes */}
                {isEditing ? (
                  <div className="group relative">
                    <FieldIcon name="notes" alignTop />
                    <textarea
                      value={student.notes || ''}
                      onChange={(e) =>
                        handleUpdateStudent(studentId, 'notes', e.target.value)
                      }
                      placeholder="Add a note..."
                      rows={2}
                      className={`${field()} resize-y py-1 pl-7 pr-2.5 text-xs`}
                    />
                  </div>
                ) : (
                  student.notes && (
                    <p className="text-xs whitespace-pre-wrap break-words text-gray-600">
                      {student.notes}
                    </p>
                  )
                )}
              </div>

              {isEditing && onMoveStudent && student._id && (
                <button
                  onClick={() => onMoveStudent(student._id, student.name)}
                  className={`${ROW_ACTION} hover:text-orange-600`}
                  title="Move student to another class"
                  aria-label={`Move ${student.name} to another class`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                </button>
              )}

              {isEditing && (
                <button
                  onClick={() => handleDeleteStudent(studentId, student.name)}
                  className={`${ROW_ACTION} hover:text-red-600`}
                  title="Remove student from this class"
                  aria-label={`Remove ${student.name} from this class`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/5">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h3 className="mb-1 text-lg font-semibold text-gray-900">Remove student</h3>
            <p className="mb-5 text-sm text-gray-600">
              Remove <span className="font-medium text-gray-900">{deleteConfirm.name}</span> from
              this class? Their results and parent login are kept.
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirmDelete}
                className="flex-1 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
              >
                Remove
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/30"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;

